/**
 * Screen Wake Lock hook.
 *
 * Acquires a screen wake lock when `active` is true and releases it when false.
 * Handles the browser visibility-change edge case: the wake lock is automatically
 * released when the page becomes hidden, so we re-acquire it when the page
 * becomes visible again (if still active).
 *
 * Fails silently if the Screen Wake Lock API is not supported.
 */

import { useEffect, useRef } from 'react';

export function useWakeLock(active: boolean): void {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    // Feature detect
    if (!('wakeLock' in navigator)) return;

    let cancelled = false;

    const acquire = async () => {
      // Don't acquire if we already hold one that isn't released
      if (wakeLockRef.current && !wakeLockRef.current.released) return;
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      } catch {
        // Silently ignore — browser may deny (e.g. low battery, background tab)
      }
    };

    const release = async () => {
      if (wakeLockRef.current && !wakeLockRef.current.released) {
        try {
          await wakeLockRef.current.release();
        } catch {
          // ignore
        }
      }
      wakeLockRef.current = null;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && active && !cancelled) {
        void acquire();
      }
    };

    if (active) {
      void acquire();
      document.addEventListener('visibilitychange', handleVisibilityChange);
    } else {
      void release();
    }

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      void release();
    };
  }, [active]);
}
